/**
 * The item tree (decision 0035): one tree, at most 4 levels (top-level = depth 0, deepest = depth 3). Pure navigation
 * over `ProjectState.items`; nothing here is stored.
 */
import { isPlannableType } from "@minga/protocol"
import { isOpenItem, itemOf } from "./state"
import type { Ids, Item, ItemId, ItemStatus, ProjectState, Run, RunId } from "./types"

/** The deepest depth below the top-level item (4 levels in total). */
export const MAX_DEPTH = 3

export const parentOf = (s: ProjectState, item: Pick<Item, "parentId">): Item | undefined =>
  item.parentId === null ? undefined : itemOf(s, item.parentId)

/** The items above, nearest first (stops at a missing parent or a cycle). */
export const ancestorsOf = (s: ProjectState, item: Pick<Item, "id" | "parentId">): ReadonlyArray<Item> => {
  const out: Item[] = []
  const seen = new Set<string>([item.id])
  let cur = parentOf(s, item)
  while (cur && !seen.has(cur.id)) {
    out.push(cur)
    seen.add(cur.id)
    cur = parentOf(s, cur)
  }
  return out
}

/** The top-level item of an item's tree (the item itself when it has no parent). */
export const topItemOf = (s: ProjectState, item: Item): Item => {
  const up = ancestorsOf(s, item)
  return up[up.length - 1] ?? item
}

export const isTopLevel = (item: Pick<Item, "parentId">): boolean => item.parentId === null

/** Depth below the top-level item: 0 for a top-level item. */
export const depthOf = (s: ProjectState, item: Pick<Item, "id" | "parentId">): number =>
  ancestorsOf(s, item).length

export const childrenOf = (s: ProjectState, id: ItemId): ReadonlyArray<Item> =>
  s.items.filter((t) => t.parentId === id)

/** Every item below, depth first (open and closed). */
export const descendantsOf = (s: ProjectState, id: ItemId): ReadonlyArray<Item> => {
  const out: Item[] = []
  const seen = new Set<string>([id])
  const walk = (pid: ItemId) => {
    for (const c of childrenOf(s, pid)) {
      if (seen.has(c.id)) continue
      seen.add(c.id)
      out.push(c)
      walk(c.id)
    }
  }
  walk(id)
  return out
}

export const openDescendants = (s: ProjectState, id: ItemId): ReadonlyArray<Item> =>
  descendantsOf(s, id).filter(isOpenItem)

/** A container has at least one child that is not cancelled (an item whose children were all cancelled is a leaf again). */
export const isContainer = (s: ProjectState, item: Pick<Item, "id">): boolean =>
  s.items.some((c) => c.parentId === item.id && c.status !== "cancelled")

/** Is the item's type plannable (release, bet, workstream)? */
export const isPlannable = (item: Pick<Item, "type">): boolean => isPlannableType(item.type)

/**
 * The planning layer shows Later / Next / Now: top-level items of plannable types and top-level containers. Every other
 * item shows Backlog / Todo / In progress (a leaf's `todo` means "runs once ready").
 */
export const isPlanningItem = (s: ProjectState, item: Item): boolean =>
  item.parentId === null && (isPlannable(item) || isContainer(s, item))

export interface Progress {
  readonly done: number
  readonly inFlight: number
  readonly blocked: number
  readonly todo: number
  readonly total: number
}

/** Leaf types that are not work left (decision 0051): an idea parked under a release, an open question (own block). */
export const NOT_COUNTED_TYPES: ReadonlySet<string> = new Set(["idea", "question"])

/**
 * The tree in one pass over `s.items` (decision 0051): `children` = parent id → children in `s.items` order (what
 * `childrenOf` returns), `containers` = ids with a child that is not cancelled (what `isContainer` says). For selectors
 * that walk many subtrees on every state change (the Overview).
 */
export interface TreeIndex {
  readonly children: ReadonlyMap<ItemId, ReadonlyArray<Item>>
  readonly containers: ReadonlySet<ItemId>
}

export const treeIndexOf = (s: ProjectState): TreeIndex => {
  const children = new Map<ItemId, Item[]>()
  const containers = new Set<ItemId>()
  for (const t of s.items) {
    if (t.parentId === null) continue
    const list = children.get(t.parentId)
    if (list) list.push(t)
    else children.set(t.parentId, [t])
    if (t.status !== "cancelled") containers.add(t.parentId)
  }
  return { children, containers }
}

/** `descendantsOf` over an index: every item below, depth first, in the same order. */
export const indexedDescendants = (ix: TreeIndex, id: ItemId): ReadonlyArray<Item> => {
  const out: Item[] = []
  const seen = new Set<string>([id])
  const walk = (pid: ItemId) => {
    for (const c of ix.children.get(pid) ?? []) {
      if (seen.has(c.id)) continue
      seen.add(c.id)
      out.push(c)
      walk(c.id)
    }
  }
  walk(id)
  return out
}

/**
 * The leaves progress counts: descendant leaves, cancelled and NOT_COUNTED_TYPES left out, in tree order. With `ix`,
 * the same walk over the index (equal results).
 */
export const progressLeaves = (s: ProjectState, id: ItemId, ix?: TreeIndex): ReadonlyArray<Item> => {
  const counted = (d: Item, container: boolean): boolean =>
    d.status !== "cancelled" && !container && !NOT_COUNTED_TYPES.has(d.type)
  return ix
    ? indexedDescendants(ix, id).filter((d) => counted(d, ix.containers.has(d.id)))
    : descendantsOf(s, id).filter((d) => counted(d, isContainer(s, d)))
}

/** The progress of a leaf list (done, in flight = in progress or in review, blocked, the rest todo). */
export const progressFromLeaves = (leaves: ReadonlyArray<Item>): Progress => {
  let done = 0
  let inFlight = 0
  let blocked = 0
  let todo = 0
  for (const l of leaves) {
    if (l.status === "done") done++
    else if (l.status === "in_progress" || l.status === "in_review") inFlight++
    else if (l.status === "blocked") blocked++
    else todo++
  }
  return { done, inFlight, blocked, todo, total: leaves.length }
}

/** Over `progressLeaves` (decision 0051: idea and question leaves don't count, on every screen). */
export const progressOf = (s: ProjectState, item: Pick<Item, "id">): Progress =>
  progressFromLeaves(progressLeaves(s, item.id))

/** The goals an item serves: its top-level item's `goalIds`. */
export const goalsOf = (s: ProjectState, item: Item): ReadonlyArray<Ids["GoalId"]> =>
  topItemOf(s, item).goalIds

/** Open and total children (the container's own progress line). */
export const childCounts = (
  s: ProjectState,
  id: ItemId,
): { readonly open: number; readonly total: number } => {
  const children = childrenOf(s, id).filter((c) => c.status !== "cancelled")
  return { open: children.filter(isOpenItem).length, total: children.length }
}

/**
 * Decision 0152: how a container's direct children are shaped, an insight for whoever plans it (never a limit).
 * `direct` children (cancelled left out), how many of them are `open`, and how many are `groups` (a release, bet or
 * workstream, or an item with children of its own), `openGroups` of them open. "67 direct, 8 open, 0 open groups"
 * says a release has gone flat.
 */
export interface PlanShape {
  readonly direct: number
  readonly open: number
  readonly groups: number
  readonly openGroups: number
}

export const planShapeOf = (s: ProjectState, id: ItemId, ix?: TreeIndex): PlanShape => {
  const children = (ix ? (ix.children.get(id) ?? []) : childrenOf(s, id)).filter(
    (c) => c.status !== "cancelled",
  )
  const isGroup = (c: Item): boolean => isPlannable(c) || (ix ? ix.containers.has(c.id) : isContainer(s, c))
  const groups = children.filter(isGroup)
  return {
    direct: children.length,
    open: children.filter(isOpenItem).length,
    groups: groups.length,
    openGroups: groups.filter(isOpenItem).length,
  }
}

/** One child's result (decision 0048): `children_closed` and `read_item` list them. */
export interface ChildResult {
  readonly ref: string
  readonly title: string
  readonly status: ItemStatus
  /** The first line (≤ 160 chars) of its last worker run's summary (an impossible claim's explanation); null without. */
  readonly outcome: string | null
  /** That run was tainted: the outcome is untrusted text. */
  readonly tainted: boolean
}

const OUTCOME_MAX = 160

const firstLine = (text: string): string | null => {
  const line =
    text
      .split("\n")
      .find((l) => l.trim().length > 0)
      ?.trim() ?? ""
  if (line.length === 0) return null
  return line.length > OUTCOME_MAX ? `${line.slice(0, OUTCOME_MAX - 1)}…` : line
}

/**
 * The children of `parentId` (in `childrenOf` order) with the outcome of each one's last worker run (`lastRunId`; a
 * validator's subject run when that is a validator). `run` looks a run up (state runs or the runs projection).
 */
export const childResults = (
  s: ProjectState,
  parentId: ItemId,
  run: (id: RunId) => Run | undefined,
): ReadonlyArray<ChildResult> =>
  childrenOf(s, parentId).map((c) => {
    let r = c.lastRunId ? run(c.lastRunId) : undefined
    if (r?.kind === "validator" && r.subjectRunId) r = run(r.subjectRunId)
    const o = r?.outcome ?? null
    const text =
      o === null
        ? null
        : o._tag === "done" || o._tag === "progress" || o._tag === "blocked"
          ? o.summary
          : o._tag === "impossible"
            ? o.explanation
            : null
    return {
      ref: c.ref,
      title: c.title,
      status: c.status,
      outcome: text === null ? null : firstLine(text),
      tainted: r?.taint === "tainted",
    }
  })

/** Has any item in the tree of `id` (itself included) had a worker attempt? */
export const workDoneIn = (s: ProjectState, id: ItemId): boolean => {
  const self = itemOf(s, id)
  return (self?.attempts ?? 0) > 0 || descendantsOf(s, id).some((d) => d.attempts > 0)
}
